import React, { useMemo } from 'react';
import { Patient, Appointment } from '../../types';
import { Icons } from '../Icons';

interface DashboardProps {
  patients: Patient[];
  appointments: Appointment[];
  userProfile?: { name: string; title: string; email: string } | null;
}

export const Dashboard: React.FC<DashboardProps> = ({ patients, appointments, userProfile }) => {
  const todayStr = new Date().toISOString().split('T')[0];

  const { todayAppointments, upcomingAppointments, nextAppointment } = useMemo(() => {
    const now = new Date();
    const todays = appointments.filter(a => a.date === todayStr);
    const upcoming = appointments
      .filter(a => a.status !== 'CANCELLED' && a.status !== 'NO_SHOW')
      .filter(a => new Date(`${a.date}T${a.time}`) >= now)
      .sort((a, b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime());

    return {
      todayAppointments: todays.length,
      upcomingAppointments: upcoming.slice(0, 4),
      nextAppointment: upcoming[0],
    };
  }, [appointments, todayStr]);

  const patientAlerts = patients.filter(p => p.alerts.length > 0).length;
  const confirmedAppointments = appointments.filter(a => a.status === 'CONFIRMED').length;
  const scheduledAppointments = appointments.filter(a => a.status === 'SCHEDULED').length;

  return (
    <div className="space-y-8">
      {/* Logged-in user profile */}
      {userProfile && (
        <div className="bg-white rounded-3xl border shadow-sm p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-700 font-black text-xl flex items-center justify-center">
              {userProfile.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-400">Logged in</p>
              <p className="text-lg font-black">{userProfile.name}</p>
              <p className="text-sm text-slate-500">{userProfile.title}</p>
              <p className="text-xs text-slate-400">{userProfile.email}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-xs font-bold uppercase text-slate-400">Overview</p>
        <h2 className="text-3xl font-black">Clinic Dashboard</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-3xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase mb-1">Total Patients</p>
            <p className="text-3xl font-black">{patients.length}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-2xl text-blue-600">
            <Icons.Users />
          </div>
        </div>

        <div className="bg-blue-600 p-6 rounded-3xl shadow-lg text-white flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-xs font-bold uppercase mb-1">Appointments</p>
            <p className="text-3xl font-black">{appointments.length}</p>
            <p className="text-xs text-blue-100 mt-1">{scheduledAppointments} scheduled / {confirmedAppointments} confirmed</p>
          </div>
          <div className="p-3 bg-white/10 rounded-2xl">
            <Icons.Calendar />
          </div>
        </div>

        <div className="bg-emerald-600 p-6 rounded-3xl shadow-lg text-white flex items-center justify-between">
          <div>
            <p className="text-emerald-100 text-xs font-bold uppercase mb-1">Today</p>
            <p className="text-3xl font-black">{todayAppointments}</p>
            <p className="text-xs text-emerald-100 mt-1">Same-day schedule</p>
          </div>
          <div className="p-3 bg-white/10 rounded-2xl">
            <Icons.Clock />
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs font-bold uppercase mb-1">Alerts</p>
            <p className="text-3xl font-black">{patientAlerts}</p>
            <p className="text-xs text-slate-500 mt-1">Patients with medical alerts</p>
          </div>
          <div className="p-3 bg-red-50 rounded-2xl text-red-600">
            <Icons.AlertCircle />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-3xl border shadow-sm p-6 space-y-4 xl:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase text-slate-400">Upcoming</p>
              <h3 className="text-xl font-black">Next Appointments</h3>
            </div>
            <span className="text-xs px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-bold">
              {upcomingAppointments.length} in queue
            </span>
          </div>

          {upcomingAppointments.length === 0 ? (
            <div className="p-6 text-center text-slate-400 border rounded-2xl">
              <p className="font-bold">No upcoming appointments</p>
              <p className="text-xs mt-1">Create a booking from the calendar or header button</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {upcomingAppointments.map(appt => (
                <div key={appt.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm">{appt.type}</p>
                      <p className="text-xs text-slate-500">{appt.date} · {appt.time}</p>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-blue-100 text-blue-700">
                      {appt.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Room: {appt.room}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border shadow-sm p-6 space-y-4">
          <div>
            <p className="text-xs font-bold uppercase text-slate-400">Snapshot</p>
            <h3 className="text-xl font-black">Next in Chair</h3>
          </div>

          {nextAppointment ? (
            <div className="p-4 bg-blue-50 rounded-2xl space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-bold">{nextAppointment.type}</p>
                <span className="px-2 py-1 rounded-lg bg-white text-blue-700 text-[10px] font-bold">
                  {nextAppointment.time}
                </span>
              </div>
              <p className="text-xs text-slate-600">Date: {nextAppointment.date}</p>
              <p className="text-xs text-slate-600">Room: {nextAppointment.room}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No upcoming appointments yet.</p>
          )}

          <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
            <p className="text-xs font-bold uppercase text-slate-400">Highlights</p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">Scheduled</span>
              <span className="text-sm font-black">{scheduledAppointments}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">Confirmed</span>
              <span className="text-sm font-black">{confirmedAppointments}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">Alerts</span>
              <span className="text-sm font-black">{patientAlerts}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
